import { useRef, useState } from "react";
import { uploadMedia } from "@/lib/storage";

interface ImageUploaderProps {
  label: string;
  value: string | null;
  onChange: (url: string) => void;
  folder: string;
  hint?: string;
  previewClassName?: string;
}

export default function ImageUploader({
  label,
  value,
  onChange,
  folder,
  hint,
  previewClassName = "h-20 w-20",
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    const result = await uploadMedia(file, folder);
    setUploading(false);
    if (result.error || !result.url) {
      setError(result.error ?? "No se pudo subir la imagen.");
      return;
    }
    onChange(result.url);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-foreground-700">{label}</span>
      <div className="flex flex-wrap items-center gap-4">
        <div
          className={`${previewClassName} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100`}
        >
          {value ? (
            <img src={value} alt={label} className="h-full w-full object-contain" />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center text-foreground-400">
              <i className="ri-image-add-line text-xl"></i>
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-60"
            >
              {uploading ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : (
                <i className="ri-upload-2-line text-sm"></i>
              )}
              {uploading ? "Subiendo..." : "Subir imagen"}
            </button>
            {value ? (
              <button
                type="button"
                onClick={() => onChange("")}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className="ri-delete-bin-line text-sm"></i>
                Quitar
              </button>
            ) : null}
          </div>
          {hint ? <span className="text-[11px] text-foreground-400">{hint}</span> : null}
          {error ? <span className="text-[11px] text-primary-600">{error}</span> : null}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </div>
  );
}